"use client";

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { motion, useTransform, useSpring, useMotionValue, AnimatePresence, useAnimationFrame } from "framer-motion";
import { GALLERY_ROW_1, GALLERY_ROW_2, PHOTOS } from "@/lib/photos";
import { ArrowUpRight, ArrowRight } from "lucide-react";

/** Descriptive alt text for lifestyle hero gallery images */
const HERO_GALLERY_ALT: Record<string, string> = {
  [PHOTOS.STAGE.bandDramaticLights]: "Band performing under dramatic stage lights at The Roostertail",
  [PHOTOS.CROWD.audienceHandsRaised]: "Yacht Rock Detroit crowd with hands raised cheering",
  [PHOTOS.STAGE.nighttimeOrangeLights]: "Nighttime performance with orange stage lights and crowd",
  [PHOTOS.DJ.behindWithCrowd]: "DJ performing with dancing crowd behind",
  [PHOTOS.CROWD.sunsetStageView]: "Sunset view of the stage and audience at Yacht Rock Detroit",
  [PHOTOS.STAGE.fullBandCaptainHats]: "Band performing in captain hats at Yacht Rock Detroit",
  [PHOTOS.DANCING.crowdHandsUpCaptainHats]: "Crowd cheering with hands up and captain hats",
  [PHOTOS.STAGE.bandSpotlightBeams]: "Live band performing on outdoor stage at Yacht Rock Detroit",
  [PHOTOS.CROWD.nightStageView]: "Night stage view with audience at Yacht Rock Detroit",
  [PHOTOS.STAGE.nighttimePurpleLights]: "Stage with purple lights and crowd at Yacht Rock Detroit",
  [PHOTOS.GROUP.fiveFriendsRoostertail]: "Friends with drinks at Yacht Rock Detroit",
  [PHOTOS.FEMALE_ONLY.fourWomenRoostertail]: "Guests posing at The Roostertail",
  [PHOTOS.COUPLE.withDrinksAtNight]: "Couple with drinks at night at Yacht Rock Detroit",
  [PHOTOS.GROUP.fourFriendsDrinks]: "Friends with drinks at The Roostertail",
  [PHOTOS.FEMALE_ONLY.fourWomenNight]: "Group of friends at Yacht Rock Detroit",
  [PHOTOS.BOAT.groupSixPeopleWaving]: "Friends waving from a boat at The Roostertail",
  [PHOTOS.GROUP.eightPeopleGazebo]: "Friends gathered under gazebo by the water",
  [PHOTOS.TWO_GUYS.hawaiianShirtsSmiling]: "Friends in Hawaiian shirts at Yacht Rock Detroit",
  [PHOTOS.FEMALE_ONLY.threeWomenDanceFloor]: "Friends dancing at Yacht Rock Detroit",
  [PHOTOS.COUPLE.posingOnDock]: "Couple posing on dock by marina",
};
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate } from "react-router-dom";

// --- Types ---
export type AnimationPhase = "scatter" | "line" | "circle" | "bottom-strip";

interface FlipCardProps {
  src: string;
  index: number;
  total: number;
  phase: AnimationPhase;
  target: { x: number; y: number; rotation: number; scale: number; opacity: number };
}

// --- FlipCard Component ---
const IMG_WIDTH = 60;
const IMG_HEIGHT = 85;

function FlipCard({
  src,
  index,
  total,
  phase,
  target,
}: FlipCardProps) {
  return (
    <motion.div
      className="absolute"
      style={{
        width: IMG_WIDTH,
        height: IMG_HEIGHT,
        transformStyle: "preserve-3d",
        perspective: 1000,
      }}
      animate={{
        x: target.x,
        y: target.y,
        rotate: target.rotation,
        scale: target.scale,
        opacity: target.opacity,
      }}
      transition={{
        type: "spring",
        stiffness: 80,
        damping: 18,
        mass: 0.8,
      }}
      whileHover={{
        scale: target.scale * 1.1,
        zIndex: 100,
      }}
    >
      <motion.div
        className="relative w-full h-full"
        style={{ transformStyle: "preserve-3d" }}
        whileHover={{ rotateY: 180 }}
        transition={{ duration: 0.6 }}
      >
        {/* Front Face */}
        <div
          className="absolute w-full h-full rounded-lg overflow-hidden shadow-xl"
          style={{ backfaceVisibility: "hidden" }}
        >
          <img
            src={src}
            alt={HERO_GALLERY_ALT[src] || "Yacht Rock Detroit event photo at The Roostertail"}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>

        {/* Back Face */}
        <div
          className="absolute w-full h-full rounded-lg bg-yacht-charcoal flex items-center justify-center"
          style={{
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          <div className="text-center p-2">
            <p className="text-yacht-cream text-[8px] font-bold">View</p>
            <p className="text-yacht-cream/60 text-[6px]">Details</p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// --- Main Hero Component ---
const TOTAL_IMAGES = 20;
const MAX_SCROLL = 3000;

// Combine gallery rows for variety
const IMAGES = [...GALLERY_ROW_1.slice(0, 10), ...GALLERY_ROW_2.slice(0, 10)];

const lerp = (start: number, end: number, t: number) => start * (1 - t) + end * t;

interface ScrollMorphHeroProps {
  title?: string;
  subtitle?: string;
  activeTitle?: string;
  activeDescription?: string;
  onMorphChange?: (value: number) => void;
}

export default function ScrollMorphHero({
  title = "Yacht rock is more than a genre.",
  subtitle = "SCROLL TO EXPLORE",
  activeTitle = "It's a Lifestyle",
  activeDescription = "Discover the smooth sounds, sunset vibes, and unforgettable moments that define the yacht rock experience.",
  onMorphChange,
}: ScrollMorphHeroProps) {
  const [introPhase, setIntroPhase] = useState<AnimationPhase>("scatter");
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  
  // Track when intro animation is complete - NO scroll until this is true
  const [introComplete, setIntroComplete] = useState(false);

  // --- Container Size ---
  useEffect(() => {
    if (!containerRef.current) return;

    const handleResize = (entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    };

    const observer = new ResizeObserver(handleResize);
    observer.observe(containerRef.current);

    setContainerSize({
      width: containerRef.current.offsetWidth,
      height: containerRef.current.offsetHeight,
    });

    return () => observer.disconnect();
  }, []);

  // --- Virtual Scroll Logic (One-way animation, no reverse) ---
  const virtualScroll = useMotionValue(0);
  const scrollRef = useRef(0);

  // Track animation state - once complete, stays complete (no reverse)
  const [animationComplete, setAnimationComplete] = useState(false);
  const [isAutoAnimating, setIsAutoAnimating] = useState(false);

  // Auto-animation using useAnimationFrame (forward only)
  useAnimationFrame(() => {
    if (!isAutoAnimating) return;

    const currentScroll = scrollRef.current;
    const target = MAX_SCROLL;
    
    // Easing speed - faster at start, slower near end
    const distance = Math.abs(target - currentScroll);
    const speed = Math.max(distance * 0.08, 15);
    
    const newScroll = Math.min(currentScroll + speed, target);
    scrollRef.current = newScroll;
    virtualScroll.set(newScroll);
    
    if (newScroll >= target * 0.99) {
      scrollRef.current = target;
      virtualScroll.set(target);
      setIsAutoAnimating(false);
      setAnimationComplete(true);
    }
  });

  // Trigger forward animation (one-time, no going back)
  const triggerForwardAnimation = useCallback(() => {
    if (isAutoAnimating || animationComplete) return;
    setIsAutoAnimating(true);
  }, [isAutoAnimating, animationComplete]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Once animation is complete, no more special scroll handling needed
    if (animationComplete) return;

    const handleWheel = (e: WheelEvent) => {
      // Block scroll during intro animation
      if (!introComplete) {
        e.preventDefault();
        return;
      }
      
      // Any scroll triggers the forward animation (one-time)
      if (e.deltaY !== 0) {
        e.preventDefault();
        triggerForwardAnimation();
      }
    };

    let touchStartY = 0;
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
    };
    
    const handleTouchMove = (e: TouchEvent) => {
      // Block touch scroll during intro animation
      if (!introComplete) {
        e.preventDefault();
        return;
      }
      
      const touchY = e.touches[0].clientY;
      const deltaY = touchStartY - touchY;
      
      // Any swipe triggers the forward animation (one-time)
      if (Math.abs(deltaY) > 5) {
        e.preventDefault();
        triggerForwardAnimation();
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    container.addEventListener("touchstart", handleTouchStart, { passive: true });
    container.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleWheel);
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
    };
  }, [introComplete, animationComplete, triggerForwardAnimation]);

  const morphProgress = useTransform(virtualScroll, [0, 600], [0, 1]);
  const smoothMorph = useSpring(morphProgress, { stiffness: 40, damping: 20 });

  const scrollRotate = useTransform(virtualScroll, [600, 3000], [0, 360]);
  const smoothScrollRotate = useSpring(scrollRotate, { stiffness: 40, damping: 20 });

  // --- Mouse Parallax ---
  const mouseX = useMotionValue(0);
  const smoothMouseX = useSpring(mouseX, { stiffness: 30, damping: 20 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const relativeX = e.clientX - rect.left;
      const normalizedX = (relativeX / rect.width) * 2 - 1;
      mouseX.set(normalizedX * 100);
    };
    container.addEventListener("mousemove", handleMouseMove);
    return () => container.removeEventListener("mousemove", handleMouseMove);
  }, [mouseX]);

  // --- Intro Sequence ---
  useEffect(() => {
    const timer1 = setTimeout(() => setIntroPhase("line"), 300);
    const timer2 = setTimeout(() => setIntroPhase("circle"), 1500);
    // Mark intro complete after circle phase settles (~2.1 seconds)
    const timer3 = setTimeout(() => setIntroComplete(true), 2100);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  // --- Random Scatter Positions ---
  const scatterPositions = useMemo(() => {
    return IMAGES.map(() => ({
      x: (Math.random() - 0.5) * 1500,
      y: (Math.random() - 0.5) * 1000,
      rotation: (Math.random() - 0.5) * 180,
      scale: 0.6,
      opacity: 0,
    }));
  }, []);

  // --- Render Loop ---
  const [morphValue, setMorphValue] = useState(0);
  const [rotateValue, setRotateValue] = useState(0);
  const [parallaxValue, setParallaxValue] = useState(0);

  useEffect(() => {
    const unsubscribeMorph = smoothMorph.on("change", (v) => {
      setMorphValue(v);
      onMorphChange?.(v);
    });
    const unsubscribeRotate = smoothScrollRotate.on("change", setRotateValue);
    const unsubscribeParallax = smoothMouseX.on("change", setParallaxValue);
    return () => {
      unsubscribeMorph();
      unsubscribeRotate();
      unsubscribeParallax();
    };
  }, [smoothMorph, smoothScrollRotate, smoothMouseX, onMorphChange]);

  // --- Content Opacity (based on rotation, not morph) ---
  const contentOpacity = useTransform(smoothScrollRotate, [120, 180], [0, 1]);
  const contentY = useTransform(smoothScrollRotate, [120, 180], [20, 0]);

  // Conditional classes based on animation state
  const outerContainerClass = animationComplete
    ? "relative w-full min-h-screen bg-yacht-blue"
    : "relative w-full h-screen overflow-hidden bg-yacht-blue";

  const innerContainerClass = animationComplete
    ? "relative w-full flex flex-col"
    : "relative w-full h-full flex items-center justify-center overflow-hidden";

  return (
    <div className={outerContainerClass}>
      {/* Container */}
      <div
        ref={containerRef}
        className={innerContainerClass}
        style={{ touchAction: animationComplete ? "auto" : "none" }}
      >
        {/* Circle Center Text - Clean text with BlurFade animation */}
        <AnimatePresence>
          {introPhase === "circle" && morphValue < 0.3 && (
            <motion.div
              className="absolute inset-0 flex flex-col items-center justify-center z-20 pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              {/* Title with blur fade animation - split into two lines */}
              <motion.div
                className="text-center px-8 mb-4"
                initial={{ y: 20, opacity: 0, filter: "blur(10px)" }}
                animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
                exit={{ y: -10, opacity: 0, filter: "blur(6px)" }}
                transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
              >
                <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl text-yacht-cream leading-tight">
                  Yacht rock is more
                  <br />
                  than a genre.
                </h1>
              </motion.div>
              
              {/* Subtitle with staggered blur fade */}
              <motion.p
                className="text-yacht-cream/50 text-xs tracking-[0.3em] uppercase"
                initial={{ y: 16, opacity: 0, filter: "blur(8px)" }}
                animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
                exit={{ y: -8, opacity: 0, filter: "blur(6px)" }}
                transition={{ duration: 0.5, delay: 0.25, ease: "easeOut" }}
              >
                {subtitle}
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Arc Active Content (Fades in when scrolling) */}
        <motion.div
          className={animationComplete 
            ? "relative w-full flex flex-col items-center text-center z-20 px-8 pt-36 md:pt-40" 
            : "absolute top-[12%] md:top-[15%] left-0 right-0 flex flex-col items-center text-center z-20 px-8 pointer-events-none"
          }
          style={{ opacity: contentOpacity, y: contentY }}
        >
          <motion.h2 
            className="font-serif italic text-4xl md:text-6xl lg:text-7xl text-yacht-yellow mb-4"
            initial={{ filter: "blur(10px)" }}
            animate={{ filter: "blur(0px)" }}
            transition={{ duration: 0.5 }}
          >
            {activeTitle}
          </motion.h2>
          <motion.p 
            className="text-yacht-cream/70 text-sm md:text-base max-w-md mb-8"
            initial={{ filter: "blur(8px)" }}
            animate={{ filter: "blur(0px)" }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {activeDescription}
          </motion.p>
        </motion.div>

        {/* Lifestyle Cards - Appear later when most photos have rotated out */}
        <motion.div
          className={animationComplete
            ? "relative w-full z-20 px-4 md:px-10 pb-12 md:pb-20"
            : "absolute top-[38%] md:top-[35%] left-0 right-0 z-20 px-4 md:px-10 pointer-events-none"
          }
          initial={{ opacity: 0 }}
          animate={{ opacity: rotateValue > 180 ? 1 : 0 }}
          transition={{ duration: 0.6, delay: rotateValue > 180 ? 0.3 : 0 }}
        >
          <div className="w-full max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-[62%_38%] gap-4 lg:gap-5 items-end">
              {/* Left Column - Large Hero Card */}
              <motion.div
                className="relative h-[339px] sm:h-[460px] lg:h-[581px] rounded-2xl overflow-hidden group cursor-pointer pointer-events-auto"
                onClick={() => navigate("/lifestyle/blog")}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: rotateValue > 180 ? 1 : 0, y: rotateValue > 180 ? 0 : 50 }}
                transition={{ duration: 0.8, delay: rotateValue > 180 ? 0.3 : 0, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                <img
                  src={PHOTOS.CROWD.sunsetStageView}
                  alt="Yacht Rock Detroit sunset crowd"
                  className="absolute inset-0 w-full h-full object-cover"
                />

                {/* Top-left Pill Badges */}
                <div className="absolute top-5 left-5 flex flex-col gap-2.5">
                  <span className="inline-flex items-center bg-white rounded-full px-4 py-2 text-xs font-medium text-yacht-charcoal">
                    July 17–18, 2026
                  </span>
                  <span className="inline-flex items-center bg-white/90 backdrop-blur-sm border border-white/50 rounded-full px-4 py-2 text-xs font-medium text-yacht-charcoal gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-yacht-teal" />
                    Festival
                  </span>
                </div>

                {/* Floating White Text Card - positioned top area */}
                <div className="absolute top-16 right-5 md:top-20 md:right-8 w-[220px] sm:w-[260px] md:w-[300px] bg-white rounded-xl p-5 md:p-6 shadow-[0_8px_30px_rgba(0,0,0,0.12)]">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-yacht-charcoal" />
                    <span className="text-xs font-medium text-yacht-charcoal/70">Music</span>
                  </div>
                  <h3 className="font-serif text-xl sm:text-2xl md:text-[28px] font-bold text-yacht-charcoal leading-[1.1]">
                    Get to your dream now destinations with Yacht Rock
                  </h3>
                </div>

                {/* Bottom-right Arrow Button */}
                <button className="absolute bottom-5 right-5 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-105 transition-transform duration-200">
                  <ArrowUpRight className="w-5 h-5 text-yacht-charcoal" />
                </button>
              </motion.div>

              {/* Right Column - Two Stacked Cards */}
              <div className="flex flex-col gap-5 mt-4 lg:mt-0">
                {/* Top-Right Card - Spotify Playlist */}
                <motion.div
                  className="relative h-[180px] sm:h-[200px] lg:h-[352px] rounded-2xl overflow-hidden pointer-events-auto"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: rotateValue > 180 ? 1 : 0, y: rotateValue > 180 ? 0 : 50 }}
                  transition={{ duration: 0.8, delay: rotateValue > 180 ? 0.4 : 0, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                  <iframe 
                    src="https://open.spotify.com/embed/playlist/5mMwgU3jW7Wxxkr5F7GzSj?utm_source=generator" 
                    width="100%" 
                    height="100%" 
                    frameBorder="0" 
                    allowFullScreen 
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" 
                    loading="lazy"
                    className="rounded-2xl"
                  />
                </motion.div>

                {/* Bottom-Right Card - Merch Card */}
                <motion.div
                  className="relative h-[200px] sm:h-[240px] lg:h-[270px] rounded-2xl overflow-hidden group cursor-pointer pointer-events-auto"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: rotateValue > 180 ? 1 : 0, y: rotateValue > 180 ? 0 : 50 }}
                  transition={{ duration: 0.8, delay: rotateValue > 180 ? 0.5 : 0, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                  <img
                    src={PHOTOS.GUEST.yellowShirtPier}
                    alt="Man in yellow Yacht Rock shirt on pier"
                    className="absolute inset-0 w-full h-full object-cover"
                  />

                  {/* Coming Soon Badge */}
                  <div className="absolute top-4 right-4 bg-yacht-yellow rounded-full px-3 py-1.5">
                    <span className="text-yacht-charcoal font-bold text-xs uppercase tracking-wide">Coming Soon</span>
                  </div>

                  <button className="absolute bottom-5 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 bg-white rounded-full px-6 py-3 md:px-8 md:py-3.5 text-sm md:text-base font-semibold text-yacht-charcoal shadow-[0_4px_20px_rgba(0,0,0,0.1)]">
                    Merch
                    <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
                  </button>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Main Container - Photos (z-10, behind cards which are z-20) - only render during animation */}
        {!animationComplete && (
          <div
            className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none"
            style={{ perspective: 1200 }}
          >
            {IMAGES.slice(0, TOTAL_IMAGES).map((src, i) => {
              let target = { x: 0, y: 0, rotation: 0, scale: 1, opacity: 1 };

              if (introPhase === "scatter") {
                target = scatterPositions[i];
              } else if (introPhase === "line") {
                const lineSpacing = 70;
                const lineTotalWidth = TOTAL_IMAGES * lineSpacing;
                const lineX = i * lineSpacing - lineTotalWidth / 2;
                target = { x: lineX, y: 0, rotation: 0, scale: 1, opacity: 1 };
              } else {
                const isMobile = containerSize.width < 768;
                const minDimension = Math.min(containerSize.width, containerSize.height);

                // Circle Position - larger radius on mobile for better spacing
                const circleRadius = isMobile 
                  ? Math.min(minDimension * 0.52, 220) 
                  : Math.min(minDimension * 0.35, 350);
                const circleAngle = (i / TOTAL_IMAGES) * 360;
                const circleRad = (circleAngle * Math.PI) / 180;
                const circlePos = {
                  x: Math.cos(circleRad) * circleRadius,
                  y: Math.sin(circleRad) * circleRadius,
                  rotation: circleAngle + 90,
                };

                // Bottom Arc Position
                const baseRadius = Math.min(containerSize.width, containerSize.height * 1.5);
                const arcRadius = baseRadius * (isMobile ? 1.4 : 1.1);
                const arcApexY = containerSize.height * (isMobile ? 0.35 : 0.25);
                const arcCenterY = arcApexY + arcRadius;

                const spreadAngle = isMobile ? 100 : 130;
                const startAngle = -90 - spreadAngle / 2;
                const step = spreadAngle / (TOTAL_IMAGES - 1);

                const scrollProgress = Math.min(Math.max(rotateValue / 360, 0), 1);
                const maxRotation = spreadAngle * 0.8;
                const boundedRotation = -scrollProgress * maxRotation;

                const currentArcAngle = startAngle + i * step + boundedRotation;
                const arcRad = (currentArcAngle * Math.PI) / 180;

                const arcPos = {
                  x: Math.cos(arcRad) * arcRadius + parallaxValue,
                  y: Math.sin(arcRad) * arcRadius + arcCenterY,
                  rotation: currentArcAngle + 90,
                  scale: isMobile ? 1.4 : 1.8,
                };

                target = {
                  x: lerp(circlePos.x, arcPos.x, morphValue),
                  y: lerp(circlePos.y, arcPos.y, morphValue),
                  rotation: lerp(circlePos.rotation, arcPos.rotation, morphValue),
                  scale: lerp(1, arcPos.scale, morphValue),
                  opacity: 1,
                };
              }

              return (
                <FlipCard
                  key={i}
                  src={src}
                  index={i}
                  total={TOTAL_IMAGES}
                  phase={introPhase}
                  target={target}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
